import { addDays, format, parseISO, subDays } from 'date-fns'
import type { Enums } from '../../db_types'
import {
  resolveCreditSetupRule,
  type CreditSetupRuleAssignmentSubject,
  type ResolvedCreditSetupRule
} from '../credit_setup_rule_assignments/resolver'

export type CreditedConsumerTeamMembership = {
  teamId: number
  effectiveFrom: string
  effectiveTo: string | null
}

export type ResolveCreditedConsumerRuleParams = {
  assignments: readonly CreditSetupRuleAssignmentSubject[]
  creditSetupId: number
  profileId: string | null
  teamMemberships: readonly CreditedConsumerTeamMembership[]
  accrualDate: string
}

export type CreditedCanonicalMetricValue = {
  id: number
  metric_id: number
  accrual_date: string
}

export type EligibleCreditedCanonicalValue<TValue extends CreditedCanonicalMetricValue> = {
  ruleId: number
  value: TValue
}

export type SelectedCreditedCanonicalValue<TValue extends CreditedCanonicalMetricValue> = {
  value: TValue
  resolution: ResolvedCreditSetupRule
}

export type CreditedMetricCalculationResult<
  TValue extends CreditedCanonicalMetricValue,
  TResult
> = {
  value: TResult | 0
  values: TValue[]
  selections: SelectedCreditedCanonicalValue<TValue>[]
}

export type CreditedConsumerRuleCoverage = {
  effectiveFrom: string
  effectiveTo: string
  resolution: ResolvedCreditSetupRule | null
}

export type CalculateCreditedMetricParams<
  TValue extends CreditedCanonicalMetricValue,
  TResult
> = ResolveCreditedConsumerRuleParams & {
  canonicalValues: readonly TValue[]
  eligibleValues: readonly EligibleCreditedCanonicalValue<TValue>[]
  aggregateFunction: Enums<'aggregate_function'>
  aggregate: (
    values: readonly TValue[],
    aggregateFunction: Enums<'aggregate_function'>
  ) => TResult | null | undefined
}

const isEffectiveOn = (membership: CreditedConsumerTeamMembership, accrualDate: string) =>
  membership.effectiveFrom <= accrualDate &&
  (membership.effectiveTo == null || membership.effectiveTo > accrualDate)

export const resolveCreditedConsumerRule = ({
  assignments,
  creditSetupId,
  profileId,
  teamMemberships,
  accrualDate
}: ResolveCreditedConsumerRuleParams): ResolvedCreditSetupRule | null =>
  resolveCreditSetupRule({
    assignments: [...assignments],
    creditSetupId,
    profileId,
    effectiveTeamIds: [
      ...new Set(
        teamMemberships
          .filter(membership => isEffectiveOn(membership, accrualDate))
          .map(membership => membership.teamId)
      )
    ],
    accrualDate
  })

const dateOnly = (date: string) => date.slice(0, 10)

const nextDate = (date: string) => format(addDays(parseISO(dateOnly(date)), 1), 'yyyy-MM-dd')

/**
 * Resolves the consumer once per effective-date interval so evidence can retain
 * uncovered gaps without expanding a Rule assignment across the whole period.
 */
export const resolveCreditedConsumerRuleCoverage = ({
  assignments,
  creditSetupId,
  profileId,
  teamMemberships,
  startDate,
  endDate
}: Omit<ResolveCreditedConsumerRuleParams, 'accrualDate'> & {
  startDate: string
  endDate: string
}): CreditedConsumerRuleCoverage[] => {
  const start = dateOnly(startDate)
  const end = dateOnly(endDate)
  const boundaryDates = new Set([start, nextDate(end)])

  for (const assignment of assignments) {
    const effectiveFrom = dateOnly(assignment.effective_from)
    const effectiveTo = assignment.effective_to ? dateOnly(assignment.effective_to) : null
    if (effectiveFrom > start && effectiveFrom <= end) boundaryDates.add(effectiveFrom)
    if (effectiveTo && effectiveTo >= start && effectiveTo < end) {
      boundaryDates.add(nextDate(effectiveTo))
    }
  }

  for (const membership of teamMemberships) {
    const effectiveFrom = dateOnly(membership.effectiveFrom)
    const effectiveTo = membership.effectiveTo ? dateOnly(membership.effectiveTo) : null
    if (effectiveFrom > start && effectiveFrom <= end) boundaryDates.add(effectiveFrom)
    if (effectiveTo && effectiveTo > start && effectiveTo <= end) {
      boundaryDates.add(effectiveTo)
    }
  }

  const boundaries = [...boundaryDates].sort()
  const coverage: CreditedConsumerRuleCoverage[] = []

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const effectiveFrom = boundaries[index]
    const effectiveTo = subDays(parseISO(boundaries[index + 1]), 1)
    const resolution = resolveCreditedConsumerRule({
      assignments,
      creditSetupId,
      profileId,
      teamMemberships,
      accrualDate: effectiveFrom
    })
    const previous = coverage.at(-1)
    const sameResolution =
      previous !== undefined &&
      previous.resolution?.assignment.id === resolution?.assignment.id &&
      previous.resolution?.ruleId === resolution?.ruleId &&
      previous.resolution?.subject === resolution?.subject

    if (sameResolution) {
      previous.effectiveTo = format(effectiveTo, 'yyyy-MM-dd')
    } else {
      coverage.push({
        effectiveFrom,
        effectiveTo: format(effectiveTo, 'yyyy-MM-dd'),
        resolution
      })
    }
  }

  return coverage
}

export const selectCreditedCanonicalValues = <TValue extends CreditedCanonicalMetricValue>({
  assignments,
  creditSetupId,
  profileId,
  teamMemberships,
  canonicalValues,
  eligibleValues
}: Omit<
  CalculateCreditedMetricParams<TValue, unknown>,
  'aggregateFunction' | 'aggregate'
>): SelectedCreditedCanonicalValue<TValue>[] => {
  const eligibleValueIdsByRule = new Map<number, Set<number>>()

  for (const eligibleValue of eligibleValues) {
    const valueIds = eligibleValueIdsByRule.get(eligibleValue.ruleId) ?? new Set<number>()
    valueIds.add(eligibleValue.value.id)
    eligibleValueIdsByRule.set(eligibleValue.ruleId, valueIds)
  }

  const selectedValueIds = new Set<number>()
  const selections: SelectedCreditedCanonicalValue<TValue>[] = []

  for (const value of canonicalValues) {
    const resolution = resolveCreditedConsumerRule({
      assignments,
      creditSetupId,
      profileId,
      teamMemberships,
      accrualDate: value.accrual_date
    })

    if (resolution == null || selectedValueIds.has(value.id)) continue
    if (!eligibleValueIdsByRule.get(resolution.ruleId)?.has(value.id)) continue

    selectedValueIds.add(value.id)
    selections.push({ value, resolution })
  }

  return selections
}

export const calculateCreditedMetric = <TValue extends CreditedCanonicalMetricValue, TResult>({
  aggregateFunction,
  aggregate,
  ...selectionParams
}: CalculateCreditedMetricParams<TValue, TResult>): CreditedMetricCalculationResult<
  TValue,
  TResult
> => {
  const selections = selectCreditedCanonicalValues(selectionParams)
  const values = selections.map(selection => selection.value)

  return {
    value: values.length === 0 ? 0 : (aggregate(values, aggregateFunction) ?? 0),
    values,
    selections
  }
}

export const aggregateCreditedMetricValues = (
  values: readonly (Pick<CreditedCanonicalMetricValue, 'accrual_date'> & {
    id?: number
    metric_value_id?: number
    value: number
  })[],
  aggregateFunction: Enums<'aggregate_function'>
): number => {
  if (values.length === 0) return 0

  switch (aggregateFunction) {
    case 'count':
      return values.length
    case 'average':
      return values.reduce((total, value) => total + value.value, 0) / values.length
    case 'min':
      return Math.min(...values.map(value => value.value))
    case 'max':
      return Math.max(...values.map(value => value.value))
    case 'latest':
      return (
        [...values]
          .sort(
            (left, right) =>
              right.accrual_date.localeCompare(left.accrual_date) ||
              right.value - left.value ||
              (right.id ?? right.metric_value_id ?? 0) - (left.id ?? left.metric_value_id ?? 0)
          )
          .at(0)?.value ?? 0
      )
    case 'sum':
    default:
      return values.reduce((total, value) => total + value.value, 0)
  }
}
